import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package } from "lucide-react";

const UNIDADES = ["NIU", "ZZ", "KGM", "MTR", "LTR", "BX", "CJ"];

export default function Productos() {
  const { empresa, formatApiError } = useApp();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { codigo: "", descripcion: "", unidad: "NIU", precio_venta: 0, precio_compra: 0, stock: 0, afecto_igv: true, categoria: "GENERAL", tipo: "PRODUCTO" };
  const [form, setForm] = useState(blank);

  const load = async () => { const { data } = await api.get("/productos"); setItems(data); };
  useEffect(() => { if (empresa) load(); /* eslint-disable-next-line */ }, [empresa]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, precio_venta: +form.precio_venta, precio_compra: +form.precio_compra, stock: +form.stock };
      if (editing) await api.put(`/productos/${editing.id}`, payload);
      else await api.post("/productos", payload);
      toast.success("Guardado"); setOpen(false); setEditing(null); setForm(blank); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const editar = (p) => { setEditing(p); setForm(p); setOpen(true); };
  const eliminar = async (p) => { if (!confirm("¿Eliminar?")) return; await api.delete(`/productos/${p.id}`); toast.success("Eliminado"); load(); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Productos y Almacén</h1>
          <p className="text-[13px] text-slate-500">Gestión de inventario, precios y stock.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); } }}>
          <DialogTrigger asChild>
            <Button data-testid="btn-nuevo-producto" className="h-9 bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="w-4 h-4 mr-1" /> Nuevo producto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} producto / servicio</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <Label className="text-[12px]">Código</Label>
                <Input data-testid="prod-codigo" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} required className="h-9 mono" />
              </div>
              <div className="col-span-2">
                <Label className="text-[12px]">Descripción</Label>
                <Input data-testid="prod-desc" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} required className="h-9" />
              </div>
              <div>
                <Label className="text-[12px]">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="PRODUCTO">Producto</SelectItem><SelectItem value="SERVICIO">Servicio</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">Unidad</Label>
                <Select value={form.unidad} onValueChange={(v) => setForm({ ...form, unidad: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNIDADES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">Categoría</Label>
                <Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-[12px]">Precio compra (S/)</Label>
                <Input type="number" step="0.01" value={form.precio_compra} onChange={(e) => setForm({ ...form, precio_compra: e.target.value })} className="h-9 tabular text-right" />
              </div>
              <div>
                <Label className="text-[12px]">Precio venta (S/)</Label>
                <Input data-testid="prod-precio" type="number" step="0.01" value={form.precio_venta} onChange={(e) => setForm({ ...form, precio_venta: e.target.value })} className="h-9 tabular text-right" />
              </div>
              <div>
                <Label className="text-[12px]">Stock inicial</Label>
                <Input type="number" step="0.01" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="h-9 tabular text-right" />
              </div>
              <div className="col-span-3 flex items-center gap-2 pt-2">
                <Switch checked={form.afecto_igv} onCheckedChange={(v) => setForm({ ...form, afecto_igv: v })} />
                <Label className="text-[12px]">Afecto a IGV (18%)</Label>
              </div>
              <DialogFooter className="col-span-3"><Button data-testid="prod-submit" type="submit" className="bg-slate-900 text-white">Guardar</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <table className="tbl w-full">
          <thead><tr><th>Código</th><th>Descripción</th><th>Tipo</th><th>Unidad</th><th className="text-right">P. Compra</th><th className="text-right">P. Venta</th><th className="text-right">Stock</th><th>IGV</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-slate-500"><Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />Sin productos registrados</td></tr>
            ) : items.map((p) => (
              <tr key={p.id}>
                <td className="mono font-medium">{p.codigo}</td>
                <td className="text-slate-800">{p.descripcion}</td>
                <td><span className="badge badge-muted">{p.tipo}</span></td>
                <td className="mono">{p.unidad}</td>
                <td className="tabular text-right">{Number(p.precio_compra).toFixed(2)}</td>
                <td className="tabular text-right font-medium">{Number(p.precio_venta).toFixed(2)}</td>
                <td className={`tabular text-right font-medium ${p.stock < 5 ? "text-rose-600" : "text-slate-800"}`}>{Number(p.stock).toFixed(2)}</td>
                <td>{p.afecto_igv ? <span className="badge badge-info">18%</span> : <span className="badge badge-muted">EXO</span>}</td>
                <td className="text-right whitespace-nowrap">
                  <button onClick={() => editar(p)} className="text-slate-600 hover:text-blue-600 mr-2"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => eliminar(p)} className="text-slate-600 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
